import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import { PlusOutlined, TeamOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { createSession, getTopics, listSessions } from "./api";
import { formatDay } from "./format";

const NewSessionModal = ({ open, meta, onClose }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [form] = Form.useForm();
  const [topics, setTopics] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      date: dayjs(),
      topics: [],
      participants: [],
      instructor: meta?.me || "",
      startTime: "",
      durationMinutes: 30,
      location: ""
    });
    getTopics()
      .then(res => setTopics(res.data.data.filter(topic => topic.active)))
      .catch(generalErrorAlert);
  }, [open]);

  const submit = async values => {
    setBusy(true);
    try {
      const res = await createSession({
        ...values,
        date: values.date.format("YYYY-MM-DD"),
        instructor: values.instructor || null
      });
      history.push(`/user/safety/sessions/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const employees = meta?.employees || [];

  return (
    <Modal
      rootClassName="safety-page"
      open={open}
      width={640}
      title={t("New safety instruction")}
      okText={t("Create")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name="topics"
          label={t("Topics")}
          rules={[{ required: true, message: t("Please choose at least one topic.") }]}
        >
          <Select
            mode="multiple"
            optionFilterProp="label"
            options={topics.map(topic => ({ value: topic._id, label: topic.title }))}
          />
        </Form.Item>
        <Form.Item label={t("Participants")}>
          <Space.Compact className="sf-full">
            <Form.Item name="participants" noStyle>
              <Select
                mode="multiple"
                optionFilterProp="label"
                options={employees.map(e => ({ value: e._id, label: e.name }))}
              />
            </Form.Item>
            <Button
              icon={<TeamOutlined />}
              onClick={() => form.setFieldsValue({ participants: employees.map(e => e._id) })}
            >
              {t("All")}
            </Button>
          </Space.Compact>
        </Form.Item>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="date" label={t("Date")} rules={[{ required: true }]}>
              <DatePicker className="sf-full" format="DD.MM.YYYY" allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              name="startTime"
              label={t("Time")}
              rules={[{ pattern: /^([01]\d|2[0-3]):[0-5]\d$/, message: "HH:mm" }]}
            >
              <Input placeholder="07:00" inputMode="numeric" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="durationMinutes" label={t("Minutes")}>
              <InputNumber className="sf-full" min={0} max={1440} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="instructor" label={t("Instructed by")}>
              <Select
                options={[
                  { value: "", label: t("Company owner") },
                  ...employees.map(e => ({ value: e._id, label: e.name }))
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="location" label={t("Location")}>
              <Input maxLength={300} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

/** List of Unterweisungen with their signature progress. */
const Sessions = ({ meta }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [status, setStatus] = useState("all");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    listSessions({ status: status === "all" ? undefined : status })
      .then(res => setData(res.data.data))
      .catch(generalErrorAlert)
      .finally(() => setLoading(false));
  }, [status]);

  return (
    <>
      <PageToolbar
        filters={
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: t("All") },
              { value: "draft", label: t("Open") },
              { value: "completed", label: t("Completed") }
            ]}
          />
        }
        actions={
          meta?.can.create && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
              {t("New safety instruction")}
            </Button>
          )
        }
      />
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        dataSource={data}
        mobileAvatar={false}
        locale={{ emptyText: t("No safety instructions yet") }}
        pagination={data.length > 50 ? { pageSize: 50 } : false}
        onRow={record => ({
          onDoubleClick: () => history.push(`/user/safety/sessions/${record._id}`)
        })}
        columns={[
          {
            title: t("Safety instruction"),
            key: "number",
            mobile: "title",
            render: (_, item) => (
              <div>
                <Link to={`/user/safety/sessions/${item._id}`}>
                  <Typography.Text strong>{item.number}</Typography.Text>
                </Link>{" "}
                <Typography.Text type="secondary">{formatDay(item.date)}</Typography.Text>
                <div className="sf-small sf-wrap">{item.topics.join(", ")}</div>
              </div>
            )
          },
          {
            title: t("Instructed by"),
            dataIndex: "instructorName",
            key: "instructor",
            mobile: "subtitle"
          },
          {
            title: t("Status"),
            key: "status",
            mobile: "badge",
            render: (_, item) =>
              item.status === "completed" ? (
                <Tag color="success">{t("Completed")}</Tag>
              ) : (
                <Tag color="processing">{t("Open")}</Tag>
              )
          },
          {
            title: t("Signed"),
            key: "signed",
            align: "right",
            render: (_, item) => `${item.signed} / ${item.participants}`
          }
        ]}
      />
      <NewSessionModal open={newOpen} meta={meta} onClose={() => setNewOpen(false)} />
    </>
  );
};

export default Sessions;
